import React, { useContext, useState } from 'react'
import DetailsBox from '../../../../../components/Cards/DetailsBox'

//@MUI
import Box from '@mui/material/Box'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import {
  ChangePresentContext,
  EventMetaContext,
  RiskAssessmentContext,
} from '../../SingleEvent'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Autocomplete from '@mui/material/Autocomplete'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CustomSelect from '../../../../../components/Select/CustomSelect'
import RisksMitigations from './RisksMitigations'
import moment from 'moment'

const RiskAssessment = ({ embedded = false }) => {
  const { eventMeta } = useContext(EventMetaContext)
  const { changesPresent, setChangesPresent } = useContext(ChangePresentContext)
  const { riskAssessment, setRiskAssessment } = useContext(
    RiskAssessmentContext,
  )
  const [accordionOpen, setAccordionOpen] = useState(
    moment().isAfter(eventMeta.endDate) || eventMeta.EMPApproved ? false : true,
  )

  const handleChange = (e) => {
    setRiskAssessment((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
    !changesPresent && setChangesPresent(true)
  }

  const form = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Autocomplete
          value={riskAssessment?.highRiskActivities ?? []}
          onChange={(e, newValue) =>
            handleChange({
              target: { value: newValue, name: 'highRiskActivities' },
            })
          }
          name="highRiskActivities"
          options={[
            'Large Crowds',
            'Equestrian',
            'Extreme Sports or Stunts',
            'Motor Racing or Motorsports',
            'Water Activities',
            'Fire Performances',
          ]}
          freeSolo
          multiple
          renderInput={(props) => (
            <TextField
              {...props}
              label="High Risk Activities"
              helperText="Add a custom tag by pressing 'enter'"
              InputLabelProps={{
                shrink: !!riskAssessment?.highRiskActivities,
              }}
            />
          )}
        />
      </Grid>
      <CustomSelect
        value={riskAssessment?.venueType}
        label="Venue Type"
        InputLabelProps={{
          shrink: !!riskAssessment?.venueType,
        }}
        options={[
          'Outdoors',
          'Racetrack',
          'Stadium',
          'Indoors',
          'Sports Field',
          'Public Highway',
          'Town Centre',
          'Other',
        ]}
        onChange={handleChange}
        name="venueType"
      />
      <CustomSelect
        value={riskAssessment?.numberAttending}
        label="Number of people attending"
        options={[
          '<500',
          '500-2000',
          '2000-5000',
          '5000-10000',
          '>10000',
        ]}
        onChange={handleChange}
        name="numberAttending"
        InputLabelProps={{
          shrink: !!riskAssessment?.numberAttending,
        }}
      />
      <CustomSelect
        value={riskAssessment?.alcoholConsumption}
        label="Alcohol Consumption"
        InputLabelProps={{
          shrink: !!riskAssessment?.alcoholConsumption,
        }}
        options={[
          'None or Minimal',
          'Social drinking',
          'Intoxication likely',
          'Intoxications expected',
        ]}
        onChange={handleChange}
        name="alcoholConsumption"
      />
      <CustomSelect
        value={riskAssessment?.recreationalDrugUse}
        label="Recreational Drug Use"
        InputLabelProps={{
          shrink: !!riskAssessment?.recreationalDrugUse,
        }}
        options={[
          'None or Minimal',
          'Isolated',
          'Intoxication likely',
          'Intoxications expected',
        ]}
        onChange={handleChange}
        name="recreationalDrugUse"
      />
      <CustomSelect
        value={riskAssessment?.activitiesRiskInjury}
        label="Risk of Injury from Activities"
        options={['None', 'Low', 'Moderate', 'High']}
        onChange={handleChange}
        name="activitiesRiskInjury"
        InputLabelProps={{
          shrink: !!riskAssessment?.activitiesRiskInjury,
        }}
      />
      <CustomSelect
        value={riskAssessment?.hospitalReferrals}
        label="Likelihood of Hospital Referrals"
        options={['Very Unlikely', 'Unlikely', 'Likely', 'High']}
        onChange={handleChange}
        name="hospitalReferrals"
        InputLabelProps={{
          shrink: !!riskAssessment?.hospitalReferrals,
        }}
      />
      <CustomSelect
        value={riskAssessment?.overnightCamping}
        label="Overnight Camping"
        options={['None', 'Crew Only', 'Some', 'Most', 'All']}
        onChange={handleChange}
        name="overnightCamping"
        InputLabelProps={{
          shrink: !!riskAssessment?.overnightCamping,
        }}
      />
      <CustomSelect
        value={riskAssessment?.prevEventData}
        label="Data from Previous Events"
        options={['First Event', 'No Data', 'Some Data', 'Good Data']}
        onChange={handleChange}
        name="prevEventData"
        InputLabelProps={{
          shrink: !!riskAssessment?.prevEventData,
        }}
      />
      {riskAssessment?.highRiskActivities?.includes('Equestrian') && (
        <>
          <CustomSelect
            value={riskAssessment?.equestrianAffiliation}
            label="Affiliation"
            options={[
              'British Eventing',
              'FEI',
              'Pony Club',
              'Unaffiliated',
              'Other',
            ]}
            onChange={handleChange}
            name="equestrianAffiliation"
            InputLabelProps={{
              shrink: !!riskAssessment?.equestrianAffiliation,
            }}
          />
          <CustomSelect
            value={riskAssessment?.equestrianDoctorPresent}
            label="Client Arranging Doctor?"
            options={['Yes', 'No']}
            onChange={handleChange}
            name="equestrianDoctorPresent"
            InputLabelProps={{
              shrink: !!riskAssessment?.equestrianDoctorPresent,
            }}
          />
          <CustomSelect
            value={riskAssessment?.equestrianXCFenceHeight}
            label="Highest XC Fence Height"
            options={[
              '60cm',
              '70cm',
              '80cm',
              '90cm',
              '100cm',
              '110cm',
              '120cm',
              '130cm',
            ]}
            onChange={handleChange}
            name="equestrianXCFenceHeight"
            InputLabelProps={{
              shrink: !!riskAssessment?.equestrianXCFenceHeight,
            }}
          />
        </>
      )}
      {riskAssessment?.highRiskActivities?.includes(
        'Motor Racing or Motorsports',
      ) && (
        <>
          <CustomSelect
            value={riskAssessment?.motorsportTrackControlled}
            label="Track Control"
            options={['Yes', 'No']}
            onChange={handleChange}
            name="motorsportTrackControlled"
            InputLabelProps={{
              shrink: !!riskAssessment?.motorsportTrackControlled,
            }}
          />
          <CustomSelect
            value={riskAssessment?.motorsportFireMarshalls}
            label="Fire Marshalls"
            options={['Yes', 'No']}
            onChange={handleChange}
            name="motorsportFireMarshalls"
            InputLabelProps={{
              shrink: !!riskAssessment?.motorsportFireMarshalls,
            }}
          />
          <CustomSelect
            value={riskAssessment?.motorsportExtricationTeam}
            label="Extrication Team"
            options={['Yes', 'No']}
            onChange={handleChange}
            name="motorsportExtricationTeam"
            InputLabelProps={{
              shrink: !!riskAssessment?.motorsportExtricationTeam,
            }}
          />
          <CustomSelect
            value={riskAssessment?.motorsportRadioToDrivers}
            label="Comms with Drivers"
            options={['Yes', 'No']}
            onChange={handleChange}
            name="motorsportRadioToDrivers"
            InputLabelProps={{
              shrink: !!riskAssessment?.motorsportRadioToDrivers,
            }}
          />
        </>
      )}
      <Grid item xs={12}>
        <TextField
          label="Level of Medical Cover Notes"
          value={riskAssessment?.levelOfMedicalCoverNote}
          onChange={handleChange}
          name="levelOfMedicalCoverNote"
          multiline
          minRows={2}
          fullWidth
          autoComplete="off"
          InputLabelProps={{
            shrink: !!riskAssessment?.levelOfMedicalCoverNote,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Risk Mitigation Notes"
          value={riskAssessment?.riskMitigationNote}
          onChange={handleChange}
          name="riskMitigationNote"
          multiline
          minRows={2}
          fullWidth
          autoComplete="off"
          InputLabelProps={{
            shrink: !!riskAssessment?.riskMitigationNote,
          }}
        />
      </Grid>
      <RisksMitigations
        riskAssessment={riskAssessment}
        handleChange={handleChange}
      />
    </Grid>
  )

  if (embedded) return form

  return (
    <Box border="solid 1px #39485e" borderRadius={1}>
      <Accordion disableGutters expanded={accordionOpen}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{ display: 'flex' }}
          onClick={() => setAccordionOpen(!accordionOpen)}
        >
          <Typography variant="title3">Risk Assessment</Typography>
        </AccordionSummary>
        <AccordionDetails>{form}</AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default React.memo(RiskAssessment)
